import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Typography, Button, IconButton, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";

const TOOLTIP_WIDTH = 340;
const WELCOME_WIDTH = 420;
const TOOLTIP_GAP = 16;
const SPOT_PADDING = 12;

const STYLES = `
  @keyframes tourCardIn {
    from { opacity: 0; transform: translateY(14px) scale(0.95); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes tourRingPulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.5; }
  }
  @keyframes tourOverlayIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .tour-card-enter {
    animation: tourCardIn 0.3s cubic-bezier(0.34, 1.4, 0.64, 1) both;
  }
  .tour-overlay-in {
    animation: tourOverlayIn 0.25s ease both;
  }
  .tour-ring {
    animation: tourRingPulse 2.4s ease-in-out infinite;
  }
`;

// Tab helpers
const openWorkbookTab = () =>
  window.dispatchEvent(new CustomEvent("tour:openTab", { detail: 1 }));
const openKpisTab = () =>
  window.dispatchEvent(new CustomEvent("tour:openTab", { detail: 0 }));

/** One-time onboarding per logged-in user */
function getMainTourStorageKey() {
  if (typeof window === "undefined") return "marketeers_tour_v1__ssr";
  const id = localStorage.getItem("userId");
  return id ? `marketeers_tour_v1__${id}` : "marketeers_tour_v1__anon";
}

const STEPS = [
  {
    icon: "👋",
    target: null,
    title: "Welcome to Smart Value!",
    content:
      "Let us take a quick tour of the platform so you can hit the ground running. It only takes a minute.",
  },
  {
    icon: "🗺️",
    target: '[data-tour="sidebar"]',
    title: "Navigation Sidebar",
    content:
      "Your main navigation lives here. Move between sections — Market Overview, Strategy Creation, and more.",
  },
  {
    icon: "📊",
    target: '[data-tour="market-overview"]',
    title: "Market Overview",
    content:
      "Monitor overall market performance and run diagnostics analysis across your categories.",
  },
  {
    icon: "🎯",
    target: '[data-tour="strategy-creation"]',
    title: "Strategy Creation",
    content:
      "Build and evaluate pricing, promotion, and assortment strategies to drive business growth.",
  },
  {
    icon: "🔄",
    target: '[data-tour="select-project"]',
    title: "Select Project",
    content:
      "Switch between different projects at any time using this button in the top bar.",
  },
  {
    icon: "📈",
    target: '[data-tour="market-kpis"]',
    title: "Market KPIs",
    content:
      "Your pinned key performance indicators — value sales, volume, price index, and more — give you an at-a-glance snapshot of market health.",
    onEnter: openKpisTab,
  },
  {
    icon: "📑",
    target: '[data-tour="workbook-tabs"]',
    title: "Workbook Tabs",
    content:
      "Use these tabs on the right edge to switch between the Market KPIs summary and the full interactive Workbook with all your detailed charts.",
  },
  {
    icon: "🔧",
    target: '[data-tour="workbook-toolbar"]',
    title: "Workbook Toolbar",
    content:
      "Reset Filters clears all applied filters across every chart instantly. Add New Chart restores any chart you previously closed.",
    onEnter: openWorkbookTab,
  },
  {
    icon: "🎛️",
    target: '[data-tour="chart-actions"]',
    title: "Chart Action Bar",
    content:
      "Every chart has an action bar at the top. Close it, get AI insights, save it to a Canvas, or drag it to reorder — all in one row.",
  },
  {
    icon: "✕",
    target: '[data-tour="chart-close"]',
    title: "Close a Chart",
    content:
      "Remove this chart from your Workbook. Closed charts move to the Add New Chart panel so you can bring them back any time.",
  },
  {
    icon: "↕️",
    target: '[data-tour="chart-drag"]',
    title: "Drag to Reorder",
    content:
      "Grab this handle and drag the chart to any position in the Workbook grid to arrange your view exactly how you want it.",
  },
  {
    icon: "💡",
    target: '[data-tour="chart-insights"]',
    title: "AI Market Insights",
    content:
      "Click the lightbulb to generate AI-powered analysis for this chart — key trends, anomalies, and opportunities, surfaced instantly.",
  },
  {
    icon: "🔖",
    target: '[data-tour="chart-save-canvas"]',
    title: "Save to Canvas",
    content:
      "Bookmark this chart with its current filters and settings to a named Canvas for later reference or team sharing.",
  },
  {
    icon: "🚀",
    target: '[data-tour="main-content"]',
    title: "You're All Set!",
    content:
      "That covers everything! Explore the platform, dive into your data, and use the tools to drive smarter decisions.",
  },
];

function computeTooltipPos(rect, cardWidth) {
  if (!rect) return null;

  const W = window.innerWidth;
  const H = window.innerHeight;
  const { x, y, width, height } = rect;
  const spaceRight = W - (x + width + SPOT_PADDING);
  const spaceLeft = x - SPOT_PADDING;
  const spaceBottom = H - (y + height + SPOT_PADDING);
  const tooltipH = 260;

  if (spaceRight >= cardWidth + TOOLTIP_GAP) {
    return {
      top: Math.max(8, Math.min(y - SPOT_PADDING, H - tooltipH - 8)),
      left: x + width + SPOT_PADDING + TOOLTIP_GAP,
    };
  }
  if (spaceLeft >= cardWidth + TOOLTIP_GAP) {
    return {
      top: Math.max(8, Math.min(y - SPOT_PADDING, H - tooltipH - 8)),
      left: x - SPOT_PADDING - TOOLTIP_GAP - cardWidth,
    };
  }
  if (spaceBottom >= tooltipH + TOOLTIP_GAP) {
    return {
      top: y + height + SPOT_PADDING + TOOLTIP_GAP,
      left: Math.max(8, Math.min(x, W - cardWidth - 8)),
    };
  }
  return {
    top: Math.max(8, y - SPOT_PADDING - tooltipH - TOOLTIP_GAP),
    left: Math.max(8, Math.min(x, W - cardWidth - 8)),
  };
}

export default function TourGuide() {
  const isFreeTrial = localStorage.getItem("role") === "free_trial";
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [cardKey, setCardKey] = useState(0);
  const navigate = useNavigate();
  const startPathRef = useRef(null);

  useEffect(() => {
    if (isFreeTrial) return undefined;
    if (localStorage.getItem(getMainTourStorageKey()) === "1") {
      return undefined;
    }
    const t = setTimeout(() => {
      if (localStorage.getItem(getMainTourStorageKey()) === "1") {
        return;
      }
      startPathRef.current = window.location.pathname;
      setActive(true);
      window.__mainTourActive = true;
    }, 900);
    return () => clearTimeout(t);
  }, []);

  const currentStep = STEPS[step];

  const refreshRect = useCallback(() => {
    if (!currentStep?.target) {
      setRect(null);
      return;
    }
    const el = document.querySelector(currentStep.target);
    setRect(el ? el.getBoundingClientRect() : null);
  }, [currentStep]);

  useEffect(() => {
    if (!active) return;
    setCardKey((k) => k + 1);

    if (currentStep.onEnter) {
      currentStep.onEnter();
      const t = setTimeout(refreshRect, 380);
      window.addEventListener("resize", refreshRect);
      window.addEventListener("scroll", refreshRect, true);
      return () => {
        clearTimeout(t);
        window.removeEventListener("resize", refreshRect);
        window.removeEventListener("scroll", refreshRect, true);
      };
    }

    refreshRect();
    window.addEventListener("resize", refreshRect);
    window.addEventListener("scroll", refreshRect, true);
    return () => {
      window.removeEventListener("resize", refreshRect);
      window.removeEventListener("scroll", refreshRect, true);
    };
  }, [active, refreshRect, step]); 

  const complete = useCallback(() => {
    localStorage.setItem(getMainTourStorageKey(), "1");
    window.__mainTourActive = false;
    setActive(false);
    openKpisTab();
    if (startPathRef.current) {
      navigate(startPathRef.current);
    }
    window.dispatchEvent(new CustomEvent("tour:mainDismissed"));
  }, [navigate]);

  const next = () =>
    step < STEPS.length - 1 ? setStep((s) => s + 1) : complete();
  const back = () => step > 0 && setStep((s) => s - 1);

  if (isFreeTrial || !active) return null;

  const isCenter = !rect;
  const isWelcome = step === 0;
  const isLast = step === STEPS.length - 1;
  const cardWidth = isWelcome ? WELCOME_WIDTH : TOOLTIP_WIDTH;
  const tooltipPos = computeTooltipPos(rect, cardWidth);
  const W = window.innerWidth;
  const H = window.innerHeight;
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 10000, pointerEvents: "none" }}>
      <style>{STYLES}</style>

      {/* ── Overlay ─────────────────────────────────────────────────────────── */}
      {isCenter ? (
        <Box
          className="tour-overlay-in"
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(3,10,30,0.72)",
            backdropFilter: "blur(2px)",
            pointerEvents: "all",
          }}
          onClick={complete}
        />
      ) : (
        <svg
          className="tour-overlay-in"
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <mask id="tour-mask">
              <rect width={W} height={H} fill="white" />
              <rect
                x={rect.x - SPOT_PADDING}
                y={rect.y - SPOT_PADDING}
                width={rect.width + SPOT_PADDING * 2}
                height={rect.height + SPOT_PADDING * 2}
                rx="10"
                fill="black"
              />
            </mask>
            {/* Soft glow filter */}
            <filter id="tour-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Dimmed overlay */}
          <rect
            width={W}
            height={H}
            fill="rgba(3,10,30,0.72)"
            mask="url(#tour-mask)"
          />

          {/* Outer glow */}
          <rect
            x={rect.x - SPOT_PADDING - 4}
            y={rect.y - SPOT_PADDING - 4}
            width={rect.width + (SPOT_PADDING + 4) * 2}
            height={rect.height + (SPOT_PADDING + 4) * 2}
            rx="13"
            fill="none"
            stroke="rgba(87,80,255,0.3)"
            strokeWidth="8"
            filter="url(#tour-glow)"
          />

          {/* Primary ring */}
          <rect
            className="tour-ring"
            x={rect.x - SPOT_PADDING}
            y={rect.y - SPOT_PADDING}
            width={rect.width + SPOT_PADDING * 2}
            height={rect.height + SPOT_PADDING * 2}
            rx="10"
            fill="none"
            stroke="#5750FF"
            strokeWidth="2.5"
          />
        </svg>
      )}

      {/* ── Tooltip card ────────────────────────────────────────────────────── */}
      <Box
        sx={
          isCenter
            ? {
                position: "fixed",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }
            : {
                position: "fixed",
                top: tooltipPos?.top,
                left: tooltipPos?.left,
                pointerEvents: "none",
              }
        }
      >
      <Paper
        key={cardKey}
        className="tour-card-enter"
        elevation={0}
        sx={{
          width: cardWidth,
          pointerEvents: "all",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow:
            "0 32px 64px rgba(0,0,0,0.28), 0 8px 24px rgba(0,0,0,0.16)",
          border: "1px solid rgba(255,255,255,0.9)",
          bgcolor: "#fff",
        }}
      >
        {/* ── Gradient accent bar ── */}
        <Box
          sx={{
            height: 4,
            background: "linear-gradient(90deg, #00265E 0%, #5750FF 100%)",
          }}
        />

        {/* ── Welcome hero (step 0 only) ── */}
        {isWelcome && (
          <Box
            sx={{
              height: 130,
              background:
                "linear-gradient(135deg, #00265E 0%, #1a3f7a 50%, #5750FF 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative blobs */}
            {[
              { top: -30, right: -20, size: 110 },
              { bottom: -25, left: -15, size: 90 },
              { top: 20, left: "30%", size: 60 },
            ].map((b, i) => (
              <Box
                key={i}
                sx={{
                  position: "absolute",
                  top: b.top,
                  bottom: b.bottom,
                  left: b.left,
                  right: b.right,
                  width: b.size,
                  height: b.size,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.07)",
                }}
              />
            ))}
            <Typography
              sx={{ fontSize: 52, lineHeight: 1, filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.3))" }}
            >
              👋
            </Typography>
          </Box>
        )}

        {/* ── Card body ── */}
        <Box sx={{ px: 3, pt: isWelcome ? 2.5 : 2, pb: 0 }}>
          {/* Row: icon + step counter + close */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.25,
            }}
          >
            {!isWelcome && (
              <Box
                sx={{
                  fontSize: 22,
                  lineHeight: 1,
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))",
                }}
              >
                {currentStep.icon}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: isWelcome ? "auto" : "auto",
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  color: "#A0ADB8",
                  fontFamily: "Poppins",
                  fontWeight: 600,
                  letterSpacing: "0.6px",
                  textTransform: "uppercase",
                }}
              >
                {step + 1} / {STEPS.length}
              </Typography>
              <IconButton
                size="small"
                onClick={complete}
                sx={{
                  p: 0.4,
                  ml: 0.5,
                  color: "#C5CDD6",
                  "&:hover": { color: "#444", bgcolor: "#F5F6F8" },
                }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isWelcome ? 20 : 16.5,
              color: "#0A1929",
              fontFamily: "Poppins",
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            {currentStep.title}
          </Typography>

          {/* Content */}
          <Typography
            sx={{
              fontSize: 13.5,
              color: "#637381",
              fontFamily: "Poppins",
              lineHeight: 1.75,
              mb: 2,
            }}
          >
            {currentStep.content}
          </Typography>
        </Box>

        {/* ── Progress bar ── */}
        <Box sx={{ px: 3, pb: 2 }}>
          <Box
            sx={{
              height: 3,
              bgcolor: "#EEF0F5",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${progress}%`,
                background:
                  "linear-gradient(90deg, #00265E 0%, #5750FF 100%)",
                borderRadius: 2,
                transition: "width 0.45s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </Box>
        </Box>

        {/* ── Footer ── */}
        <Box
          sx={{
            px: 3,
            pb: 2.5,
            pt: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: step > 0 ? "space-between" : "flex-end",
          }}
        >
          {step > 0 && (
            <Button
              startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
              onClick={back}
              sx={{
                textTransform: "none",
                color: "#8796A5",
                fontFamily: "Poppins",
                fontSize: 13.5,
                fontWeight: 500,
                px: 1.5,
                borderRadius: "8px",
                "&:hover": { bgcolor: "#F5F6F8", color: "#00265E" },
              }}
            >
              Back
            </Button>
          )}

          <Button
            variant="contained"
            endIcon={
              !isLast ? <ArrowForwardIcon sx={{ fontSize: 14 }} /> : undefined
            }
            onClick={next}
            sx={{
              textTransform: "none",
              background: isLast
                ? "linear-gradient(135deg, #5750FF 0%, #7B75FF 100%)"
                : "linear-gradient(135deg, #00265E 0%, #1a4080 100%)",
              fontFamily: "Poppins",
              fontSize: 13.5,
              fontWeight: 600,
              px: 2.5,
              py: 0.9,
              borderRadius: "8px",
              boxShadow: isLast
                ? "0 4px 14px rgba(87,80,255,0.4)"
                : "0 4px 14px rgba(0,38,94,0.3)",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "translateY(-1px)",
                boxShadow: isLast
                  ? "0 6px 18px rgba(87,80,255,0.5)"
                  : "0 6px 18px rgba(0,38,94,0.4)",
              },
            }}
          >
            {isLast ? "Start Exploring" : "Next"}
          </Button>
        </Box>
      </Paper>
      </Box>

      {/* ── Skip tour pill ── */}
      {!isLast && (
        <Box
          sx={{
            position: "fixed",
            bottom: 28,
            left: "50%",
            transform: "translateX(-50%)",
            pointerEvents: "all",
          }}
        >
          <Button
            onClick={complete}
            sx={{
              color: "rgba(255,255,255,0.75)",
              textTransform: "none",
              fontFamily: "Poppins",
              fontSize: 13,
              fontWeight: 500,
              px: 3,
              py: 0.75,
              borderRadius: "999px",
              border: "1.5px solid rgba(255,255,255,0.3)",
              bgcolor: "rgba(0,0,0,0.25)",
              backdropFilter: "blur(6px)",
              letterSpacing: "0.2px",
              transition: "all 0.2s ease",
              "&:hover": {
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.12)",
                border: "1.5px solid rgba(255,255,255,0.7)",
              },
            }}
          >
            Skip tour
          </Button>
        </Box>
      )}
    </Box>
  );
}
